import { Component, Structure } from '@wix/thunderbolt-becky-types'
import * as _ from 'lodash'
import { PINNED_LAYER_SUFFIX } from '../constants'
import {
	PinnedPositions,
	checkIfPinnedComponentAndAddToMap,
	createComponent,
	isFixed,
} from './utils/pinnedComponentsUtils'
import {
	GROUP_TYPES,
	ComponentsByType,
	createPinnedGroup,
	createSOAPGroup,
	createStructuralGroup,
	PINNED_GROUPS_IDS,
	MasterPageGroup,
	getIsStructural,
} from './utils/masterPageUtils'

const isHeaderOrFooter = (compId: string) => compId === 'SITE_HEADER' || compId === 'SITE_FOOTER'
const shouldAddPlaceholder = (compId: string, layout: Component['layout'], isRemoveHeaderFooterWrappers: boolean) =>
	isHeaderOrFooter(compId) && isFixed(layout) && !isRemoveHeaderFooterWrappers
const getHeaderOrFooterWrapperId = (compId: string) => `${compId}_WRAPPER`

const sortMasterPageChildrenIntoGroups = (
	childrenIds: Array<string>,
	childrenLayout: { [compId: string]: Component['layout'] },
	isMobileView: boolean,
	isRemoveHeaderFooterWrappersOpen: boolean
): ComponentsByType => {
	const isStructural = getIsStructural({ isMobileView, removeWrappers: isRemoveHeaderFooterWrappersOpen })
	const pagesContainerIdx = childrenIds.findIndex((childId) => childId === 'PAGES_CONTAINER')

	return childrenIds.reduce(
		(acc, childId, idx) => {
			if (isStructural(childId)) {
				acc.structural.push(childId)
			} else {
				const childLayout = childrenLayout[childId]
				const isPinned = checkIfPinnedComponentAndAddToMap(childId, childLayout, acc.pinned)
				if (!isPinned && !isFixed(childLayout)) {
					acc.soap[idx < pagesContainerIdx ? 'beforeMain' : 'afterMain'].push(childId)
				}
			}

			return acc
		},
		{
			pinned: {},
			soap: {
				beforeMain: [],
				afterMain: [],
			},
			structural: [],
		} as ComponentsByType
	)
}

const getMasterPageChildrenGroups = (
	childrenIds: Array<string>,
	childrenLayout: { [compId: string]: Component['layout'] },
	isMobileView: boolean,
	isRemoveHeaderFooterWrappersOpen: boolean
): Array<MasterPageGroup> => {
	const componentsByType = sortMasterPageChildrenIntoGroups(
		childrenIds,
		childrenLayout,
		isMobileView,
		isRemoveHeaderFooterWrappersOpen
	)

	const createPinnedGroupIfNotEmpty = (position: PinnedPositions, parent: 'SITE_HEADER' | 'SITE_FOOTER') =>
		componentsByType.pinned[position] &&
		createPinnedGroup(
			componentsByType.pinned[position]!,
			PINNED_GROUPS_IDS[position],
			isRemoveHeaderFooterWrappersOpen ? 'masterPage' : getHeaderOrFooterWrapperId(parent)
		)

	return [
		createPinnedGroupIfNotEmpty('TOP_LEFT', 'SITE_HEADER'),
		createPinnedGroupIfNotEmpty('TOP_CENTER', 'SITE_HEADER'),
		createPinnedGroupIfNotEmpty('TOP_RIGHT', 'SITE_HEADER'),
		componentsByType.structural.includes('SITE_HEADER') && createStructuralGroup('SITE_HEADER'),
		componentsByType.structural.includes('SOSP_CONTAINER_CUSTOM_ID') &&
			createStructuralGroup('SOSP_CONTAINER_CUSTOM_ID'),
		createPinnedGroupIfNotEmpty('CENTER_LEFT', 'SITE_HEADER'),
		createPinnedGroupIfNotEmpty('CENTER_RIGHT', 'SITE_HEADER'),
		componentsByType.soap.beforeMain.length > 0 &&
			createSOAPGroup(componentsByType.soap.beforeMain, 'soapBeforePagesContainer'),
		componentsByType.structural.includes('PAGES_CONTAINER') && createStructuralGroup('PAGES_CONTAINER'),
		componentsByType.soap.afterMain.length > 0 &&
			createSOAPGroup(componentsByType.soap.afterMain, 'soapAfterPagesContainer'),
		createPinnedGroupIfNotEmpty('BOTTOM_LEFT', 'SITE_FOOTER'),
		createPinnedGroupIfNotEmpty('BOTTOM_CENTER', 'SITE_FOOTER'),
		createPinnedGroupIfNotEmpty('BOTTOM_RIGHT', 'SITE_FOOTER'),
		componentsByType.structural.includes('SITE_FOOTER') && createStructuralGroup('SITE_FOOTER'),
		componentsByType.structural.includes('BACK_TO_TOP_BUTTON') && createStructuralGroup('BACK_TO_TOP_BUTTON'),
		componentsByType.structural.includes('QUICK_ACTION_BAR') && createStructuralGroup('QUICK_ACTION_BAR'),
	].filter((group) => group) as Array<MasterPageGroup>
}

const addPinnedGroupToStructure = (
	structureComps: Structure,
	group: MasterPageGroup,
	isRemoveHeaderFooterWrappersOpen: boolean
) => {
	const isBeforeHeader = () =>
		[PINNED_GROUPS_IDS.TOP_LEFT, PINNED_GROUPS_IDS.TOP_CENTER, PINNED_GROUPS_IDS.TOP_RIGHT].includes(group.id!)
	const isBeforeFooter = () =>
		[PINNED_GROUPS_IDS.BOTTOM_LEFT, PINNED_GROUPS_IDS.BOTTOM_CENTER, PINNED_GROUPS_IDS.BOTTOM_RIGHT].includes(
			group.id!
		)

	const components: Array<string> = []
	_.forEach(group.components, (id) => {
		const pinnedId = `${id}${PINNED_LAYER_SUFFIX}`
		structureComps[pinnedId] = createComponent(pinnedId, [id], group.id!, 'PinnedLayer', 'Container')
		components.push(pinnedId)
	})

	const groupParentComponents = structureComps[group.parent!].components!
	structureComps[group.id!] = createComponent(group.id!, components, group.parent!, 'DivWithChildren')

	if (isRemoveHeaderFooterWrappersOpen) {
		if (isBeforeHeader()) {
			const siteHeaderIndex = groupParentComponents.findIndex((compId) => compId === 'SITE_HEADER')
			groupParentComponents.splice(siteHeaderIndex, 0, group.id!)
		} else if (isBeforeFooter()) {
			const siteFooterIndex = groupParentComponents.findIndex((compId) => compId === 'SITE_FOOTER')
			groupParentComponents.splice(siteFooterIndex, 0, group.id!)
		}
		// Between header and footer
		else {
			const siteHeaderIndex = groupParentComponents.findIndex((compId) => compId === 'SITE_HEADER')
			groupParentComponents.splice(siteHeaderIndex + 1, 0, group.id!)
		}
	} else {
		groupParentComponents.push(group.id!)
	}
}

const addGroupedCompsToStructure = (
	structureComps: Structure,
	masterPageChildrenGroups: Array<MasterPageGroup>,
	childrenLayout: { [compId: string]: Component['layout'] },
	isRemoveHeaderFooterWrappersOpen: boolean
) => {
	_.forEach(masterPageChildrenGroups, (group) => {
		if (group.id) {
			// nonStructuralGroup
			if (group.type === GROUP_TYPES.PINNED) {
				addPinnedGroupToStructure(structureComps, group, isRemoveHeaderFooterWrappersOpen)
			} else {
				structureComps[group.id] = createComponent(
					group.id,
					group.components,
					'masterPage',
					'MeshGroup',
					'Container'
				)
			}
		} else if (!isRemoveHeaderFooterWrappersOpen) {
			const compId = group.components[0]
			if (isHeaderOrFooter(compId)) {
				const wrapperId = getHeaderOrFooterWrapperId(compId)
				structureComps[wrapperId].components!.push(compId)
			}
			if (shouldAddPlaceholder(compId, childrenLayout[compId], false)) {
				const placeholderId = `${compId}-placeholder`
				structureComps[placeholderId] = {
					id: placeholderId,
					componentType: 'DivWithChildren',
					layout: { height: childrenLayout[compId].height },
					type: 'Component',
					parent: 'masterPage',
				} as Component
			}
		}
	})

	return structureComps
}

const createHeaderAndFooterWrappers = () => {
	const headerWrapperId = getHeaderOrFooterWrapperId('SITE_HEADER')
	const footerWrapperId = getHeaderOrFooterWrapperId('SITE_FOOTER')

	return {
		[headerWrapperId]: createComponent(headerWrapperId, [], 'masterPage', 'ContainerWrapper'),
		[footerWrapperId]: createComponent(footerWrapperId, [], 'masterPage', 'ContainerWrapper'),
	}
}

const convertMasterPageGroupsIntoStructure = (
	masterPage: Structure['masterPage'],
	masterPageGroups: Array<MasterPageGroup>,
	childrenLayout: { [compId: string]: Component['layout'] },
	isRemoveHeaderFooterWrappers: boolean
): Structure => {
	const masterPageChildrenGroups = _.flatMap(masterPageGroups, (childrenGroup) => {
		const compId = childrenGroup.components[0]
		if (shouldAddPlaceholder(compId, childrenLayout[compId], isRemoveHeaderFooterWrappers)) {
			return {
				...childrenGroup,
				components: childrenGroup.components.concat(`${compId}-placeholder`),
			}
		}
		return childrenGroup
	})

	const nonPinnedMasterPageChildrenGroups = _.filter(
		masterPageChildrenGroups,
		(group) => group.type !== GROUP_TYPES.PINNED
	)
	const masterPageChildren = _.flatMap(nonPinnedMasterPageChildrenGroups, (group) => {
		if (group.type === GROUP_TYPES.SOAP) {
			return group.id
		} else {
			const components = group.components.slice(0)
			return _.map(components, (compId) => {
				if (isHeaderOrFooter(compId) && !isRemoveHeaderFooterWrappers) {
					return getHeaderOrFooterWrapperId(compId)
				}
				return compId
			})
		}
	})

	const structureComps = {
		masterPage: { ...masterPage, components: masterPageChildren },
		...(!isRemoveHeaderFooterWrappers && createHeaderAndFooterWrappers()),
	} as Structure
	return addGroupedCompsToStructure(
		structureComps,
		masterPageChildrenGroups,
		childrenLayout,
		isRemoveHeaderFooterWrappers
	)
}

export { getMasterPageChildrenGroups, convertMasterPageGroupsIntoStructure, sortMasterPageChildrenIntoGroups }
